import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Save, Upload, X, Eye, Loader } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { businessAPI } from '../../services/api';
import { useBusiness } from '../../contexts/BusinessContext';
import DocumentTemplate from '../../components/DocumentTemplate';

const PrintTemplateSettings = () => {
  const { activeBusiness } = useBusiness();
  const [settings, setSettings] = useState({
    logo: null,
    fontFamily: 'Inter',
    primaryColor: '#3B82F6',
    showGSTIN: true,
    showHSN: true,
    showDiscount: true,
    showTax: true,
    footerText: 'This is a computer generated document'
  });

  const [showPreview, setShowPreview] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (activeBusiness?.id) {
      loadSettings();
    }
  }, [activeBusiness]);

  const loadSettings = async () => {
    if (!activeBusiness?.id) return;
    
    try {
      setLoading(true);
      const response = await businessAPI.getPrintSettings(activeBusiness.id);
      setSettings(response.data);
      if (response.data.logo) {
        setLogoPreview(response.data.logo);
      }
    } catch (error) {
      console.error('Failed to load settings:', error);
      // Silently fail and keep default settings
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!activeBusiness?.id) {
      toast.error('No active business selected');
      return;
    }

    try {
      setSaving(true);
      await businessAPI.updatePrintSettings(activeBusiness.id, settings);
      toast.success('Template settings saved successfully');
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error('Logo file size should be less than 2MB');
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result;
        setSettings(prev => ({ ...prev, logo: base64 }));
        setLogoPreview(base64);
        toast.success('Logo uploaded successfully');
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveLogo = () => {
    setSettings(prev => ({ ...prev, logo: null }));
    setLogoPreview(null);
    toast.success('Logo removed');
  };

  const fontOptions = [
    { value: 'Inter', label: 'Inter (Modern Sans)' },
    { value: 'Poppins', label: 'Poppins (Clean Sans)' },
    { value: 'Roboto', label: 'Roboto (Classic Sans)' },
    { value: 'Georgia', label: 'Georgia (Serif)' },
    { value: 'Times New Roman', label: 'Times New Roman (Traditional)' }
  ];

  const colorPresets = [
    { name: 'Blue', value: '#3B82F6' },
    { name: 'Indigo', value: '#6366F1' },
    { name: 'Purple', value: '#9333EA' },
    { name: 'Green', value: '#10B981' },
    { name: 'Red', value: '#EF4444' },
    { name: 'Orange', value: '#F59E0B' },
    { name: 'Gray', value: '#6B7280' }
  ];

  // Sample document for preview
  const sampleDocument = {
    doc_no: 'QT-202512-0001',
    doc_date: new Date().toISOString(),
    party: {
      name: 'ABC Enterprises',
      phone: '9876543210',
      gstin: '29ABCDE1234F1Z5',
      billing_address: {
        line1: '123 Main Street',
        city: 'Bangalore',
        state: 'Karnataka',
        pincode: '560001'
      }
    },
    items: [
      {
        line_no: 1,
        name: 'Sample Product',
        description: 'High quality product',
        hsn_sac: '8517',
        qty: 10,
        unit: 'PCS',
        price: 100,
        discount_type: 'PERCENT',
        discount_value: 10,
        tax_rate: 18,
        line_total: 1062
      }
    ],
    charges: [],
    totals: {
      subtotal: 1000,
      discount_total: 100,
      taxable_amount: 900,
      tax_total: 162,
      round_off: 0,
      grand_total: 1062
    },
    tax_breakup: [
      { tax_rate: 18, taxable: 900, tax: 162 }
    ],
    terms: {
      notes_public: 'Thank you for your business',
      terms_lines: ['Payment due within 30 days', 'Goods once sold will not be taken back']
    },
    signature: {
      authorized_name: 'John Doe'
    },
    meta: {}
  };

  const sampleBusiness = {
    business_name: 'YOUR COMPANY NAME',
    tagline: 'Your Business Tagline',
    gstin: '29ABCDE1234F1Z5',
    address: {
      line1: '123 Business Street',
      city: 'Bangalore',
      state: 'Karnataka',
      pincode: '560001'
    },
    phone: '080-12345678',
    email: 'info@yourcompany.com'
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Print Template Settings</h1>
        <p className="text-gray-600 mt-1">Customize your document templates for quotations, invoices, and other documents</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Settings Panel */}
        <div className="space-y-6">
          {/* Logo Upload */}
          <Card>
            <CardHeader>
              <CardTitle>Company Logo</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {logoPreview ? (
                <div className="relative">
                  <img src={logoPreview} alt="Logo Preview" className="h-24 object-contain border border-gray-200 rounded-lg p-2" />
                  <button
                    onClick={handleRemoveLogo}
                    className="absolute -top-2 -right-2 bg-red-500 text-white p-1 rounded-full hover:bg-red-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                  <Upload className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-600 mb-2">Upload your company logo</p>
                  <p className="text-xs text-gray-500 mb-4">PNG, JPG up to 2MB</p>
                  <label className="cursor-pointer">
                    <span className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 inline-block">
                      Choose File
                    </span>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      className="hidden"
                    />
                  </label>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Typography */}
          <Card>
            <CardHeader>
              <CardTitle>Typography</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Font Family</Label>
                <select
                  value={settings.fontFamily}
                  onChange={(e) => setSettings(prev => ({ ...prev, fontFamily: e.target.value }))}
                  className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  {fontOptions.map(font => (
                    <option key={font.value} value={font.value}>{font.label}</option>
                  ))}
                </select>
              </div>
            </CardContent>
          </Card>

          {/* Colors */}
          <Card>
            <CardHeader>
              <CardTitle>Brand Colors</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Primary Color</Label>
                <div className="flex gap-2 mt-2">
                  {colorPresets.map(color => (
                    <button
                      key={color.value}
                      onClick={() => setSettings(prev => ({ ...prev, primaryColor: color.value }))}
                      className={`w-10 h-10 rounded-lg border-2 transition-all ${
                        settings.primaryColor === color.value ? 'border-gray-900 scale-110' : 'border-gray-200'
                      }`}
                      style={{ backgroundColor: color.value }}
                      title={color.name}
                    />
                  ))}
                </div>
                <div className="mt-3">
                  <Input
                    type="color"
                    value={settings.primaryColor}
                    onChange={(e) => setSettings(prev => ({ ...prev, primaryColor: e.target.value }))}
                    className="h-10 w-full"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Visibility Options */}
          <Card>
            <CardHeader>
              <CardTitle>Field Visibility</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <Label htmlFor="showGSTIN" className="cursor-pointer">Show GSTIN</Label>
                <Switch
                  id="showGSTIN"
                  checked={settings.showGSTIN}
                  onCheckedChange={(checked) => setSettings(prev => ({ ...prev, showGSTIN: checked }))}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label htmlFor="showHSN" className="cursor-pointer">Show HSN/SAC Column</Label>
                <Switch
                  id="showHSN"
                  checked={settings.showHSN}
                  onCheckedChange={(checked) => setSettings(prev => ({ ...prev, showHSN: checked }))}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label htmlFor="showDiscount" className="cursor-pointer">Show Discount Column</Label>
                <Switch
                  id="showDiscount"
                  checked={settings.showDiscount}
                  onCheckedChange={(checked) => setSettings(prev => ({ ...prev, showDiscount: checked }))}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label htmlFor="showTax" className="cursor-pointer">Show Tax Column</Label>
                <Switch
                  id="showTax"
                  checked={settings.showTax}
                  onCheckedChange={(checked) => setSettings(prev => ({ ...prev, showTax: checked }))}
                />
              </div>
            </CardContent>
          </Card>

          {/* Footer Text */}
          <Card>
            <CardHeader>
              <CardTitle>Footer Text</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                value={settings.footerText}
                onChange={(e) => setSettings(prev => ({ ...prev, footerText: e.target.value }))}
                placeholder="Enter footer text..."
                rows={2}
                className="w-full"
              />
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <Button onClick={handleSave} className="flex-1" disabled={saving || loading}>
              {saving ? (
                <>
                  <Loader className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Settings
                </>
              )}
            </Button>
            <Button onClick={() => setShowPreview(!showPreview)} variant="outline" className="flex-1" disabled={loading}>
              <Eye className="w-4 h-4 mr-2" />
              {showPreview ? 'Hide' : 'Show'} Preview
            </Button>
          </div>
        </div>

        {/* Preview Panel */}
        {showPreview && (
          <div className="lg:sticky lg:top-6">
            <Card>
              <CardHeader>
                <CardTitle>Live Preview</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="border border-gray-300 rounded-lg overflow-hidden" style={{ transform: 'scale(0.4)', transformOrigin: 'top left', width: '250%', height: '350mm' }}>
                  <DocumentTemplate
                    documentType="QUOTATION"
                    document={sampleDocument}
                    business={sampleBusiness}
                    templateSettings={settings}
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default PrintTemplateSettings;
